import React, { useState } from "react";
import type { ComponentType, SVGProps } from "react";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import type { ToggleGroupSize } from "@design-system/ToggleGroup/ToggleGroup";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { BoardViewIcon, ListViewIcon } from "@icons";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import sectionStyles from "./CheckboxPlayground.module.scss";
import styles from "./DatePickerPlayground.module.scss";

const toggleGroupSizes = [
    "sm",
    "md",
    "lg",
] as const satisfies readonly ToggleGroupSize[];

type Size = (typeof toggleGroupSizes)[number];

const options = ["day", "week", "month"] as const;

type Option = (typeof options)[number];

type Layout = "list" | "board";

interface LayoutOption {
    value: Layout;
    label: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const layoutOptions: readonly LayoutOption[] = [
    { value: "list", label: "List view", Icon: ListViewIcon },
    { value: "board", label: "Board view", Icon: BoardViewIcon },
];

/** The `button` variant: icon-only squares with tooltips, as on Tasks. */
const ButtonVariantDemo = () => {
    const [layout, setLayout] = useState<Layout>("list");

    return (
        <VStack className={sectionStyles.section} gap={16} padding={24}>
            <h2 className={sectionStyles.sectionTitle}>Button variant</h2>
            <ToggleGroup.Root<Layout>
                variant="button"
                aria-label="Layout"
                value={layout}
                onValueChange={(newLayout) => newLayout && setLayout(newLayout)}
            >
                {layoutOptions.map(({ value, label, Icon }) => (
                    <Tooltip key={value} content={label}>
                        <ToggleGroup.Item value={value} aria-label={label}>
                            <Icon aria-hidden />
                        </ToggleGroup.Item>
                    </Tooltip>
                ))}
            </ToggleGroup.Root>
        </VStack>
    );
};

export const ToggleGroupPlayground = () => {
    const [size, setSize] = useState<Size>("md");
    const [value, setValue] = useState<Option>("day");
    const [hasDisabledItem, setHasDisabledItem] = useState(false);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root
                            value={size}
                            onValueChange={(newSize) => {
                                if (newSize) {
                                    setSize(newSize);
                                }
                            }}
                        >
                            {toggleGroupSizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Disabled item"
                                checked={hasDisabledItem}
                                onChange={setHasDisabledItem}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${size} · ${value}`}
                belowStage={<ButtonVariantDemo />}
                output={JSON.stringify(
                    {
                        size,
                        value,
                        disabledItem: hasDisabledItem ? "week" : null,
                    },
                    null,
                    2,
                )}
            >
                <ToggleGroup.Root
                    size={size}
                    value={value}
                    onValueChange={(newValue) => {
                        if (newValue) {
                            setValue(newValue);
                        }
                    }}
                >
                    {options.map((option, index) => (
                        <ToggleGroup.Item
                            key={option}
                            value={option}
                            disabled={hasDisabledItem && index === 1}
                        >
                            {option}
                        </ToggleGroup.Item>
                    ))}
                </ToggleGroup.Root>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
